import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Calculator, Info } from "lucide-react";

/**
 * PricingSimulator (Msg 694 revision).
 *
 * Simplified view for partner — only shows what THE PARTNER earns per commission tier.
 * DiTours fee and motorista payout are HIDDEN from the partner.
 */
export default function PricingSimulator({ platformFeePct = 15 }) {
  const [categories, setCategories] = useState([]);
  const [multipliers, setMultipliers] = useState([]);
  const [adminPrice, setAdminPrice] = useState(60);
  const [selectedCat, setSelectedCat] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [catsResp] = await Promise.all([
          api.get("/public/vehicle-categories-v2"),
        ]);
        const cats = (catsResp.data || []).filter((c) => c.active);
        setCategories(cats);
        if (cats.length > 0) {
          setSelectedCat(cats[0].key);
          setAdminPrice(Math.max(60, Number(cats[0].base_price_eur) || 60));
        }
        try {
          const mr = await api.get("/partner/pricing/multipliers");
          setMultipliers(mr.data || []);
        } catch {
          setMultipliers([
            { pct: 5, multiplier: 1.053 }, { pct: 10, multiplier: 1.111 },
            { pct: 15, multiplier: 1.176 }, { pct: 20, multiplier: 1.25 },
            { pct: 25, multiplier: 1.333 }, { pct: 30, multiplier: 1.429 },
            { pct: 35, multiplier: 1.538 }, { pct: 40, multiplier: 1.667 },
            { pct: 45, multiplier: 1.818 }, { pct: 50, multiplier: 2.0 },
          ]);
        }
      } catch (e) { /* silent */ }
    })();
  }, []);

  const rows = useMemo(() => {
    const admin = Number(adminPrice) || 0;
    return multipliers.map((r) => {
      const client = admin * Number(r.multiplier || 1);
      const partnerEarn = client * Number(r.pct || 0) / 100;
      return { pct: r.pct, multiplier: r.multiplier, client, partner: partnerEarn };
    });
  }, [adminPrice, multipliers]);

  return (
    <div className="border border-slate-200 bg-white p-5" data-testid="partner-pricing-simulator">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="w-4 h-4 text-[#f59e0b]" />
        <h3 className="text-sm font-bold uppercase tracking-widest text-[#0a0f1e]">Simulador de Preços</h3>
      </div>
      <p className="text-xs text-slate-600 mb-4 max-w-2xl">
        Escolha uma categoria e o preço base — veja quanto <strong>ganha</strong> por reserva em cada nível de comissão.
        Use isto para decidir a sua percentagem ideal em <strong>Markup</strong>.
      </p>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <label className="block text-[9px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Categoria</label>
          <select
            value={selectedCat}
            onChange={(e) => {
              const key = e.target.value;
              setSelectedCat(key);
              const cat = categories.find((c) => c.key === key);
              if (cat) setAdminPrice(Math.max(60, Number(cat.base_price_eur) || 60));
            }}
            className="h-9 px-2 border border-slate-300 focus:border-[#f59e0b] focus:outline-none text-sm"
            data-testid="sim-category"
          >
            {categories.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Preço base (€)</label>
          <Input
            type="number" min="0" step="1"
            value={adminPrice}
            onChange={(e) => setAdminPrice(Number(e.target.value) || 0)}
            className="w-28 h-9 text-right"
            data-testid="sim-admin-price"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
              <th className="p-2 text-left">Se cobrar…</th>
              <th className="p-2 text-right">Cliente paga</th>
              <th className="p-2 text-right">Você ganha</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const highlight = r.pct === 15;
              return (
                <tr key={r.pct} className={`border-b border-slate-100 ${highlight ? "bg-[#fff8ec]/60" : ""}`} data-testid={`sim-row-${r.pct}`}>
                  <td className="p-2">
                    <div className="text-sm font-bold text-[#0a0f1e]">{r.pct}%</div>
                  </td>
                  <td className="p-2 text-right font-mono text-[#0a0f1e] font-bold">€{r.client.toFixed(2)}</td>
                  <td className="p-2 text-right font-mono text-emerald-700 font-bold text-base">€{r.partner.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-start gap-2 text-[10px] text-slate-500 bg-slate-50 p-2 border border-slate-100">
        <Info className="w-3 h-3 text-[#f59e0b] mt-0.5 flex-shrink-0" />
        <div>
          Estes valores aplicam-se quando o parceiro tem <strong>modo %</strong> no perfil (Markup).
          Escolha a percentagem que melhor equilibra volume e receita para o seu negócio.
        </div>
      </div>
    </div>
  );
}
